import { useMemo, useState } from 'react'

import { useTransientEventBadge } from '../../hooks/useTransientEventBadge'
import { Estimate } from '../../models/Estimate'
import { Gift } from '../../models/Gift'
import { Item } from '../../models/Item'
import { Line } from '../../models/Line'
import { RecordFactory } from '../../models/RecordFactory'
import { Taxcode } from '../../models/Taxcode'
import { User } from '../../models/User'
import { currencyFormat } from '../../utils/numbers'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import { ClientSearchableSelect } from '../contacts/ClientSearchableSelect'
import { ContractSearchableSelect } from '../items/ContractSearchableSelect'
import { TypeSearchableSelect } from '../items/TypeSearchableSelect'
import { Chip } from '../common/fields/Chip'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { TextArea } from '../common/fields/TextArea'
import { Receipt, WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    estimate: Estimate
    owner: User
    onUpdateEstimate: (updatedEstimate: Estimate) => void
    items: Item[]
}

export const NewEstimatePage = ({ estimate, onUpdateEstimate, owner, items }: Props) => {
    const itemLines = estimate.lines.filter((line) => line.type === 'item')
    const expenseLines = estimate.lines.filter((line) => line.type === 'expense')

    const [currentEditingExpenseLine, setCurrentEditingExpenseLine] = useState<
        { type: 'new'; line: Line } | { type: 'edit'; line: Line; lineIndex: number } | null
    >(null)

    const { isBadgeShowing: isItemAddedBadgeShowing, onEvent: onItemAdded } =
        useTransientEventBadge()

    const handleSelectClient = async (user: User | undefined) => {
        const client = await RecordFactory.getInstance().loadClient(user?.id ?? '')

        const newChannel = !client?.notify
            ? undefined
            : client.notify === 'sms' && !!user?.phone
              ? 'sms'
              : client.notify === 'email' && !!user?.email
                ? 'email'
                : client.notify === 'combo' && !!user?.phone && !!user?.email
                  ? 'combo'
                  : ''

        if (client !== undefined && (client.discount ?? 0) > 0) {
            const updatedEstimateObj = estimate.clone({
                client: user,
                discount_type: 'percent',
                discount: client.discount,
            })
            if (newChannel !== undefined) {
                updatedEstimateObj.channel = newChannel
            }
            onUpdateEstimate(updatedEstimateObj)
        }
        else {
            const updatedEstimateObj = estimate.clone({ client: user })
            if (newChannel !== undefined) {
                updatedEstimateObj.channel = newChannel
            }
            onUpdateEstimate(updatedEstimateObj)
        }
    }

    const itemsSearchableItems = useMemo(() => {
        return items.map((item) => ({
            id: item.id ?? '',
            title: item.name ?? '',
            subtitle: (
                <>
                    <span className={item.discountAmount !== undefined ? 'line-through' : ''}>
                        {currencyFormat(item.rate)}
                    </span>{' '}
                    {item.rate !== undefined && item.discountAmount !== undefined
                        ? currencyFormat(item.rate - item.discountAmount)
                        : ''}
                </>
            ),
            tertiaryView:
                item.image?.uri !== undefined ? (
                    <img src={item.image.uri} className="size-9 rounded-full" />
                ) : item.type === 'gift' ? (
                    <Icon icon="gift.small" background="round" className="size-9" />
                ) : item.type === 'package' ? (
                    <Icon icon="package2.small" background="round" className="size-9" />
                ) : item.type === 'service' ? (
                    <Icon icon="tag.small" background="round" className="size-9" />
                ) : undefined,
            item,
        }))
    }, [items])
    const handleSelectItemOption = (itemOption: (typeof itemsSearchableItems)[number] | null) => {
        if (itemOption === null) {
            return
        }

        const itemLine = Line.initWithItem(
            itemOption.item.clone({ quantity: 1, memo: '', allow_salestax: true }),
            estimate
        )
        itemLine.quantity = 1
        itemLine.total = itemOption.item.rate ?? 0
        itemLine.taxable =
            itemOption.item.type === 'product' && Taxcode.defaultProductTaxable(owner.state)
        itemLine.discountable = itemOption.item.type !== 'product'
        itemLine.memo = ''

        const newLines = [...estimate.lines, itemLine]
        if ((itemOption.item.discount ?? 0) > 0 && estimate.discount === undefined) {
            onUpdateEstimate(
                estimate.clone({
                    lines: newLines,
                    discount_type: itemOption.item.discountType,
                    discount: itemOption.item.discount,
                })
            )
        }
        else {
            onUpdateEstimate(
                estimate.clone({
                    lines: newLines,
                })
            )
        }
        onItemAdded()
    }
    const handleUpdateItemLineAtIndex = (index: number, newLine: Line) => {
        const lineToUpdate = itemLines[index]
        if (lineToUpdate === undefined) {
            return
        }

        const newSalesTaxInfo = newLine.taxable
            ? {
                  nexus: 'pro',
                  taxcode: owner.taxcode,
                  tax: owner.taxcode?.rate,
              }
            : {}

        onUpdateEstimate(
            estimate.clone({
                lines: estimate.lines.map((line) => (line === lineToUpdate ? newLine : line)),
                ...newSalesTaxInfo,
            })
        )
    }
    const handleRemoveItemLineAtIndex = (index: number) => {
        const lineToRemove = itemLines[index]
        if (lineToRemove === undefined) {
            return
        }
        onUpdateEstimate(
            estimate.clone({
                lines: estimate.lines.filter((line) => line !== lineToRemove),
            })
        )
    }

    const handleAddExpense = () => {
        const expenseLine = Line.initWithType('expense', estimate)
        expenseLine.total = 0
        expenseLine.quantity = 1
        expenseLine.taxable = owner.allowSalesTax
        expenseLine.discountable = owner.allowDiscount

        setCurrentEditingExpenseLine({ type: 'new', line: expenseLine })
    }
    const handleUpdateExpenseLineAtIndex = (index: number, newLine: Line) => {
        const lineToUpdate = expenseLines[index]
        if (lineToUpdate === undefined) {
            return
        }
        onUpdateEstimate(
            estimate.clone({
                lines: estimate.lines.map((line) => (line === lineToUpdate ? newLine : line)),
            })
        )
    }
    const handleRemoveExpenseLineAtIndex = (index: number) => {
        const lineToRemove = expenseLines[index]
        if (lineToRemove === undefined) {
            return
        }
        onUpdateEstimate(
            estimate.clone({
                lines: estimate.lines.filter((line) => line !== lineToRemove),
            })
        )
    }

    const handleUpdateGift = (gift: Gift | undefined) => {
        if (gift?.balance) {
            onUpdateEstimate(
                estimate.clone({
                    discount_amount: gift.balance,
                    discount_type: 'rate',
                })
            )
        }
        else {
            onUpdateEstimate(
                estimate.clone({
                    discount_amount: undefined,
                    discount_type: 'percent',
                })
            )
        }
    }

    const hasDeposit = (estimate.deposit ?? 0) > 0

    const receipt = useMemo((): Receipt => {
        return {
            modifiers: {
                subtotal: {
                    amount: estimate.total ?? 0,
                    onUpdate:
                        itemLines.length === 0 && expenseLines.length === 0
                            ? (newAmount) =>
                                  onUpdateEstimate(
                                      estimate.clone({
                                          rate: Math.max(newAmount - (estimate.deposit ?? 0), 0),
                                      })
                                  )
                            : undefined,
                },
                deposit: {
                    amount: estimate.deposit !== undefined ? estimate.deposit : null,
                    onChange: (newValue) => {
                        onUpdateEstimate(
                            estimate.clone({
                                rate: Math.max((estimate.total ?? 0) - (newValue ?? 0), 0),
                                deposit: newValue,
                                confirm_status:
                                    (newValue ?? 0) > 0 ? 'confirmPayment' : estimate.confirmStatus,
                            })
                        )
                    },
                },
                discount: owner.allowDiscount ? {} : undefined,
                salesTax: owner.allowSalesTax
                    ? {
                          amount: estimate.taxAmount ?? null,
                          parenthetical:
                              estimate.taxcode !== undefined
                                  ? `${estimate.taxcode.rate ?? 0}%`
                                  : null,
                      }
                    : undefined,
                surcharge: owner.allowSurcharge
                    ? {
                          amount: estimate.allowSurcharge
                              ? (estimate.surchargeAmount ?? null)
                              : null,
                          onAdd: () => onUpdateEstimate(estimate.clone({ allow_surcharge: true })),
                          onRemove: () =>
                              onUpdateEstimate(estimate.clone({ allow_surcharge: false })),
                      }
                    : undefined,
            },
            total: estimate.askingAmount,
        }
    }, [estimate])

    return (
        <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
            <WorkflowPage.Card title="General">
                <WorkflowPage.IconSection title="Client (required)" icon="user.large">
                    <ClientSearchableSelect
                        mode="server"
                        selectedClient={estimate.client}
                        selectedSubaccount={estimate.subaccount}
                        onSelectClient={handleSelectClient}
                        onSelectSubaccount={(subaccount) =>
                            onUpdateEstimate(
                                estimate.clone({ client: subaccount?.client, subaccount })
                            )
                        }
                        shouldPreserveLastSelection={true}
                        canAddNew
                        shouldIncludeSubaccounts
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="Status"
                    icon="info.large"
                    description="Select the estimate approval status"
                >
                    <div className="flex flex-wrap items-center justify-start gap-x-2 gap-y-1">
                        <Chip
                            title="Already approved"
                            isSelected={(estimate.confirmStatus ?? '') === ''}
                            onClick={() =>
                                onUpdateEstimate(
                                    estimate.clone({ confirm_status: '', status: 'confirmed' })
                                )
                            }
                            size="small"
                            disabled={hasDeposit}
                        />
                        <Chip
                            title="Requires approval"
                            isSelected={(estimate.confirmStatus ?? '') === 'confirmOnly'}
                            onClick={() =>
                                onUpdateEstimate(
                                    estimate.clone({
                                        confirm_status: 'confirmOnly',
                                        status: 'sentQuote',
                                    })
                                )
                            }
                            size="small"
                            disabled={hasDeposit}
                        />
                        <Chip
                            title="Requires credit card"
                            isSelected={(estimate.confirmStatus ?? '') === 'confirmPayment'}
                            onClick={() =>
                                onUpdateEstimate(
                                    estimate.clone({
                                        confirm_status: 'confirmPayment',
                                        status: 'sentQuote',
                                    })
                                )
                            }
                            size="small"
                        />
                    </div>
                </WorkflowPage.IconSection>

                {owner.allowItemize && (
                    <>
                        <WorkflowPage.IconSection
                            title="Items"
                            icon="listPlus.large"
                            secondaryText={isItemAddedBadgeShowing ? 'Added' : undefined}
                            secondaryTextClassName="font-medium text-success-400"
                        >
                            <SearchableSelect
                                items={itemsSearchableItems}
                                onSelectItem={handleSelectItemOption}
                                titleWeight="bold"
                                placeholder="Type to search"
                                shouldRefocusAfterSelection
                            />

                            {itemLines.map((line, lineIndex) => (
                                <WorkflowPage.ItemLine
                                    key={lineIndex}
                                    line={line}
                                    onUpdate={(newLine) =>
                                        handleUpdateItemLineAtIndex(lineIndex, newLine)
                                    }
                                    onRemove={() => handleRemoveItemLineAtIndex(lineIndex)}
                                />
                            ))}
                        </WorkflowPage.IconSection>

                        <WorkflowPage.IconSection
                            title="Expenses"
                            icon="coins.large"
                            description="Select"
                            secondaryView={
                                <Button
                                    onClick={handleAddExpense}
                                    intent="tertiaryLink"
                                    className="hover:text-primary-700/60 mr-2 translate-x-3"
                                    size="small"
                                >
                                    Add
                                </Button>
                            }
                            hasHoverEffect
                        >
                            {expenseLines.length > 0 && (
                                <div className="flex flex-col items-stretch justify-start gap-y-3">
                                    {expenseLines.map((line, lineIndex) => (
                                        <WorkflowPage.ExpenseLine
                                            key={lineIndex}
                                            line={line}
                                            onUpdate={(newLine) =>
                                                handleUpdateExpenseLineAtIndex(lineIndex, newLine)
                                            }
                                            onRemove={() =>
                                                handleRemoveExpenseLineAtIndex(lineIndex)
                                            }
                                            onEdit={() =>
                                                setCurrentEditingExpenseLine({
                                                    type: 'edit',
                                                    line,
                                                    lineIndex,
                                                })
                                            }
                                        />
                                    ))}
                                </div>
                            )}
                        </WorkflowPage.IconSection>

                        {currentEditingExpenseLine !== null && (
                            <WorkflowPage.EditExpenseLineModal
                                isOpen
                                onClose={() => setCurrentEditingExpenseLine(null)}
                                onDone={(line) => {
                                    switch (currentEditingExpenseLine.type) {
                                        case 'new': {
                                            onUpdateEstimate(
                                                estimate.clone({
                                                    lines: [...estimate.lines, line],
                                                })
                                            )
                                            break
                                        }
                                        case 'edit': {
                                            handleUpdateExpenseLineAtIndex(
                                                currentEditingExpenseLine.lineIndex,
                                                line
                                            )
                                            break
                                        }
                                    }
                                    setCurrentEditingExpenseLine(null)
                                }}
                                line={currentEditingExpenseLine.line}
                            />
                        )}
                    </>
                )}
            </WorkflowPage.Card>

            <WorkflowPage.Card title="Total">
                <WorkflowPage.ReceiptView
                    receipt={receipt}
                    transaction={estimate}
                    onUpdateTransaction={onUpdateEstimate}
                    client={estimate.client}
                    owner={owner}
                />
            </WorkflowPage.Card>

            <WorkflowPage.NotificationsSection
                transaction={estimate}
                onUpdateTransaction={onUpdateEstimate}
                sendViaDescription="Choose how you’d like your client to be sent this estimate"
                reminderDescription="If an estimate remains unconfirmed, chose how often you'd like your client to be politely reminded until they respond."
            />

            <WorkflowPage.Card title="More options" defaultIsCollapsed>
                <WorkflowPage.IconSection
                    title="Memo"
                    icon="note.large"
                    secondaryText={`${(estimate.memo ?? '').length} / 1500`}
                >
                    <TextArea
                        size="medium"
                        isAutoResizing
                        placeholder="Add a memo"
                        className="overlow-y-hidden"
                        value={estimate.memo ?? ''}
                        onChange={(event) =>
                            onUpdateEstimate(estimate.clone({ memo: event.target.value }))
                        }
                        maxLength={1500}
                        onClear={() => onUpdateEstimate(estimate.clone({ memo: '' }))}
                    />
                </WorkflowPage.IconSection>

                {owner.allowRecord && (
                    <WorkflowPage.IconSection title="Attach a form" icon="field.large">
                        <TypeSearchableSelect
                            owner={owner}
                            selectedForm={estimate.form}
                            onSelectForm={(form) => onUpdateEstimate(estimate.clone({ form }))}
                        />
                    </WorkflowPage.IconSection>
                )}

                {owner.allowRecord && (
                    <WorkflowPage.IconSection title="Attach a contract" icon="pen.large">
                        <ContractSearchableSelect
                            selectedContract={estimate.contract}
                            onSelectContract={(contract) =>
                                onUpdateEstimate(estimate.clone({ contract }))
                            }
                        />
                    </WorkflowPage.IconSection>
                )}
            </WorkflowPage.Card>
        </div>
    )
}
